/**
 * Staff Evaluations functional pass: the evaluation lifecycle.
 *
 * Templates are versioned: a published version never changes, and every
 * cycle records the version it runs under. Scales, domains, components,
 * weighting, the overall rule, required observations and conferences, goal
 * rules, the approval chain and signatures are all template data. A cycle
 * moves through stages with its own deadlines; what the employee sees is
 * decided by release state, never by which screen they open. Ratings and
 * employment actions stay human decisions: nothing here scores a person.
 */

import type { BaseDocument } from '../platform/documents.js'

// ─────────────────────────────────────────────────────────────────────────────
// Templates
// ─────────────────────────────────────────────────────────────────────────────

export const EMPLOYEE_TYPES = ['teacher', 'principal', 'assistant_principal', 'counselor', 'nurse', 'classified', 'custodian', 'transportation', 'food_service', 'administrative', 'central_office', 'substitute', 'custom'] as const
export type EmployeeType = (typeof EMPLOYEE_TYPES)[number]
export const EMPLOYEE_TYPE_LABELS: Record<EmployeeType, string> = {
  teacher: 'Teacher',
  principal: 'Principal',
  assistant_principal: 'Assistant Principal',
  counselor: 'Counselor',
  nurse: 'Nurse',
  classified: 'Classified Staff',
  custodian: 'Custodian',
  transportation: 'Transportation',
  food_service: 'Food Service',
  administrative: 'Administrative',
  central_office: 'Central Office',
  substitute: 'Substitute',
  custom: 'Custom',
}

export const TEMPLATE_STATUSES = ['draft', 'under_review', 'published', 'retired', 'archived'] as const
export type TemplateStatus = (typeof TEMPLATE_STATUSES)[number]

export interface RatingLevel {
  /** Numeric value used only when the template's weighting calls for it. */
  value: number
  label: string
  descriptor?: string | null
}

export interface RatingScale {
  key: string
  label: string
  /** Lowest to highest. */
  levels: RatingLevel[]
  allowNotObserved: boolean
}

export interface EvalComponent {
  key: string
  label: string
  description?: string | null
  weight?: number | null
  ratingRequired: boolean
  narrativeRequired: boolean
  evidenceRequired: boolean
  /** Level value → what that rating looks like for this component. */
  descriptors?: Record<string, string>
  sequence: number
}

export interface EvalDomain {
  key: string
  label: string
  description?: string | null
  weight?: number | null
  /** A domain-level rating and narrative, in addition to (or instead of) components. */
  ratingRequired: boolean
  narrativeRequired: boolean
  components: EvalComponent[]
  sequence: number
}

export const OBSERVATION_TYPES = ['formal', 'informal', 'walkthrough', 'conference', 'site_visit', 'operational_review', 'custom'] as const
export type ObservationType = (typeof OBSERVATION_TYPES)[number]
export const CONFERENCE_TYPES = ['pre_observation', 'post_observation', 'mid_year', 'goal_review', 'final', 'improvement_plan_review', 'custom'] as const
export type ConferenceType = (typeof CONFERENCE_TYPES)[number]

export const CYCLE_STAGES = ['self_assessment', 'goals', 'observation', 'mid_year', 'observation_2', 'final_conference', 'final_evaluation', 'signature'] as const
export type CycleStage = (typeof CYCLE_STAGES)[number]

/** How a deadline is computed. */
export type EvalDeadlineRule =
  | { kind: 'fixed'; date: string }
  | { kind: 'after_start'; days: number; unit: 'calendar' | 'business' | 'school' }
  | { kind: 'before_due'; days: number; unit: 'calendar' | 'business' | 'school' }

export interface SignatureRule {
  role: 'employee' | 'evaluator' | 'reviewer' | 'administrator'
  order: number
  meaning: string
  /** The employee may record a refusal instead of signing. */
  refusalAllowed?: boolean
  refusalWitnessRequired?: boolean
}

export interface ApprovalRuleStep {
  key: string
  label: string
  /** evaluator_supervisor: the evaluator's own supervisor from Staff. */
  approver: { kind: 'evaluator_supervisor' } | { kind: 'roles'; roles: string[] } | { kind: 'staff'; staffIds: string[] }
}

export interface TemplateConfig {
  scale: RatingScale
  domains: EvalDomain[]
  weighting: 'none' | 'domain' | 'component'
  overall: { mode: 'calculated' | 'manual' | 'hybrid' | 'none'; overrideAllowed: boolean; overrideRequiresApproval: boolean }
  requiredObservations: { type: ObservationType; count: number; announced?: 'announced' | 'unannounced' | 'either' }[]
  requiredConferences: ConferenceType[]
  goals: { min: number; max: number; approvalRequired: boolean; types: string[] }
  selfAssessment: { required: boolean; evaluatorSeesOn: 'submit' | 'release' }
  /** Walkthroughs count toward ratings only when explicitly included. */
  walkthroughsRollUp: 'never' | 'when_included'
  unannouncedAllowed: boolean
  approvalChain: ApprovalRuleStep[]
  signatures: SignatureRule[]
  deadlines: { stage: CycleStage; rule: EvalDeadlineRule }[]
  improvementPlan: { mayRecommendBelow?: number | null; requiresHrApproval: boolean }
  evaluatorQualification?: { training?: string | null; warnOnly: boolean } | null
}

/** One version of a template. A published version is immutable; changes make the next version. */
export interface EvaluationTemplateVersion extends BaseDocument {
  docType: 'evaluationTemplateVersion'
  family: string
  name: string
  employeeTypes: EmployeeType[]
  version: number
  status: TemplateStatus
  effectiveStart: string
  effectiveEnd?: string | null
  schoolYear?: string | null
  /** District-locked: schools may not customise; otherwise listed fields only. */
  districtLocked: boolean
  schoolCustomizable: string[]
  config: TemplateConfig
  publishedAt?: string | null
  publishedByName?: string | null
  supersedesId?: string | null
  history: { at: string; byName: string; action: string }[]
}

export const templateVersionId = (family: string, version: number) => `evtv-${family}-v${version}`

/** What weights must add up to for the template's weighting mode. */
export function weightingIssues(c: Pick<TemplateConfig, 'weighting' | 'domains'>): string[] {
  const out: string[] = []
  if (c.weighting === 'domain') {
    const sum = c.domains.reduce((n, d) => n + (d.weight ?? 0), 0)
    if (Math.round(sum) !== 100) out.push(`Domain weights add up to ${sum}, not 100.`)
  }
  if (c.weighting === 'component') {
    for (const d of c.domains) {
      const sum = d.components.reduce((n, x) => n + (x.weight ?? 0), 0)
      if (d.components.length && Math.round(sum) !== 100) out.push(`${d.label}: component weights add up to ${sum}, not 100.`)
    }
  }
  return out
}

/** Everything wrong with a template before it may be published. */
export function templateIssues(t: Pick<EvaluationTemplateVersion, 'name' | 'employeeTypes' | 'config'>): string[] {
  const out: string[] = []
  const c = t.config
  if (!t.name.trim()) out.push('The template needs a name.')
  if (!t.employeeTypes.length) out.push('Choose at least one employee type.')
  if (c.scale.levels.length < 2) out.push('A rating scale needs at least two levels.')
  if (new Set(c.scale.levels.map((l) => l.value)).size !== c.scale.levels.length) out.push('Two rating levels share a value.')
  if (!c.domains.length) out.push('Add at least one domain.')
  const keys = c.domains.flatMap((d) => [d.key, ...d.components.map((x) => `${d.key}.${x.key}`)])
  if (new Set(keys).size !== keys.length) out.push('Two domains or components share a key.')
  if (c.domains.some((d) => !d.components.length && !d.ratingRequired && !d.narrativeRequired)) out.push('Every domain needs components, or its own rating or narrative.')
  out.push(...weightingIssues(c))
  if (c.overall.mode === 'calculated' && c.weighting === 'none' && c.domains.length > 1) out.push('A calculated overall rating needs weighting; choose domain or component weights, or make the overall manual.')
  if (!c.signatures.some((s) => s.role === 'employee')) out.push('The employee must sign (or be able to record a refusal).')
  if (c.goals.min > c.goals.max) out.push('The minimum number of goals is more than the maximum.')
  return out
}

/** Template mapping: the most specific rule that fits wins. */
export interface TemplateMappingRule {
  key: string
  family: string
  when: { employeeTypes?: EmployeeType[]; positionCodes?: string[]; schoolIds?: string[]; departments?: string[]; contractTypes?: string[] }
  priority: number
}

export function pickTemplateFamily(rules: readonly TemplateMappingRule[], f: { employeeType: EmployeeType; positionCode?: string | null; schoolId?: string | null; department?: string | null; contractType?: string | null }, defaultFamily: string | null): { family: string | null; ruleKey: string | null } {
  const fits = rules.filter((r) => {
    const w = r.when
    if (w.employeeTypes?.length && !w.employeeTypes.includes(f.employeeType)) return false
    if (w.positionCodes?.length && (!f.positionCode || !w.positionCodes.includes(f.positionCode))) return false
    if (w.schoolIds?.length && (!f.schoolId || !w.schoolIds.includes(f.schoolId))) return false
    if (w.departments?.length && (!f.department || !w.departments.includes(f.department))) return false
    if (w.contractTypes?.length && (!f.contractType || !w.contractTypes.includes(f.contractType))) return false
    return true
  })
  const best = [...fits].sort((a, b) => b.priority - a.priority || specificity(b) - specificity(a))[0]
  return best ? { family: best.family, ruleKey: best.key } : { family: defaultFamily, ruleKey: null }
}
const specificity = (r: TemplateMappingRule) => Object.values(r.when).filter((v) => Array.isArray(v) && v.length).length

/** Employee type from position: the default the mapping starts from. */
export function employeeTypeOf(s: { isTeacher?: boolean; positionTitle?: string | null; department?: string | null; workerType?: string | null }): EmployeeType {
  const t = `${s.positionTitle ?? ''} ${s.department ?? ''}`.toLowerCase()
  if (s.workerType === 'substitute' || /substitute/.test(t)) return 'substitute'
  if (/assistant principal/.test(t)) return 'assistant_principal'
  if (/principal/.test(t)) return 'principal'
  if (/counsel/.test(t)) return 'counselor'
  if (/nurse/.test(t)) return 'nurse'
  if (/custod|maintenance/.test(t)) return 'custodian'
  if (/driver|transport/.test(t)) return 'transportation'
  if (/food|cafeteria|nutrition/.test(t)) return 'food_service'
  if (/superintendent|director|coordinator|human resources|payroll|finance|technology/.test(t)) return 'central_office'
  if (s.isTeacher || /teacher|instructor/.test(t)) return 'teacher'
  if (/secretar|clerk|registrar|office/.test(t)) return 'administrative'
  return 'classified'
}

// ─────────────────────────────────────────────────────────────────────────────
// Cycles
// ─────────────────────────────────────────────────────────────────────────────

export const CYCLE_STATUSES = ['not_started', 'self_assessment', 'goals', 'observation', 'mid_year_review', 'final_evaluation', 'awaiting_employee_signature', 'awaiting_evaluator_signature', 'awaiting_approval', 'finalized', 'reopened', 'cancelled'] as const
export type CycleStatus = (typeof CYCLE_STATUSES)[number]
export const CYCLE_STATUS_LABELS: Record<CycleStatus, string> = {
  not_started: 'Not Started',
  self_assessment: 'Self-Assessment',
  goals: 'Goals',
  observation: 'Observation',
  mid_year_review: 'Mid-Year Review',
  final_evaluation: 'Final Evaluation',
  awaiting_employee_signature: 'Awaiting Employee Signature',
  awaiting_evaluator_signature: 'Awaiting Evaluator Signature',
  awaiting_approval: 'Awaiting Approval',
  finalized: 'Finalized',
  reopened: 'Reopened',
  cancelled: 'Cancelled',
}

export const EVALUATOR_SOURCES = ['direct_supervisor', 'school_principal', 'department_chair', 'designated', 'manual'] as const
export type EvaluatorSource = (typeof EVALUATOR_SOURCES)[number]

export interface CycleDeadline {
  stage: CycleStage
  dueOn: string
  /** The date before any adjustment (leave, transfer) — never overwritten. */
  originalDueOn: string
  adjustments: { at: string; byName: string; from: string; to: string; reason: string }[]
}

export interface PersonSnapshot {
  staffId: string
  name: string
  staffNumber: string | null
  position: string | null
  school: string | null
  schoolId: string | null
  department: string | null
  at: string
}

/** Fields a cycle carries beyond Thadeus's (the `evaluation` document is extended, not replaced). */
export interface CycleV2Fields {
  name: string
  templateFamily: string
  templateVersionId: string
  templateVersion: number
  employeeType: EmployeeType
  cycleStatus: CycleStatus
  stage: CycleStage | null
  startDate: string
  dueDate: string
  secondaryEvaluatorStaffId?: string | null
  secondaryRole?: 'co_evaluator' | 'reviewer' | 'administrator' | null
  supervisorStaffId?: string | null
  evaluatorSource: EvaluatorSource
  evaluatorQualificationWarning?: string | null
  deadlines: CycleDeadline[]
  /** Raised when the employee's position, supervisor or school changes mid-cycle. */
  flags: { kind: 'assignment_review' | 'offboarding' | 'leave'; reason: string; at: string; resolvedAt?: string | null; resolvedByName?: string | null }[]
  employeeSnapshot: PersonSnapshot
  evaluatorSnapshot: PersonSnapshot & { title: string | null }
  /** Release state: what the employee may see of the evaluator's work. */
  sharedAt?: string | null
  bulkBatchId?: string | null
  history: { at: string; byName: string; action: string; note?: string | null }[]
  // ── Stage 2 ──
  final?: import('./evaluationEngine.js').FinalEvaluation | null
  /** Bumped every time the content to be signed changes; signatures name the version they signed. */
  contentVersion?: number
  contentHash?: string | null
  signatures?: import('./evaluationEngine.js').EvalSignature[]
  /** Signatures voided by a return or reopen — never deleted. */
  signatureHistory?: (import('./evaluationEngine.js').EvalSignature & { voidedAt: string; voidedReason: string })[]
  /** The employee's own responses; nobody else writes here. */
  responses?: { at: string; byName: string; text: string; kind: 'response' | 'rebuttal' | 'comment'; attachment?: { blobPath: string; fileName: string } | null }[]
  /** The evaluator's later notes, kept apart from the employee's. */
  addenda?: { at: string; byName: string; text: string }[]
  approvalSteps?: { key: string; label: string; status: 'pending' | 'approved' | 'denied' | 'returned' | 'skipped'; requires: { module?: string; action?: string; roles?: string[]; userIds?: string[] }; byName?: string | null; byUserId?: string | null; at?: string | null; comment?: string | null }[]
  returnInfo?: { at: string; byName: string; areas: string[]; comment: string } | null
  infoRequests?: { at: string; byName: string; text: string; answeredAt?: string | null; answer?: string | null }[]
  finalizedAt?: string | null
  /** The frozen document the PDF prints from — what was signed, as it was. */
  finalDocument?: Record<string, unknown> | null
  /** Earlier finalized versions, kept when a cycle is reopened. */
  finalizations?: { version: number; finalizedAt: string; reopenedAt: string; reopenedByName: string; reason: string; document: Record<string, unknown> }[]
}

/** Stage order for a template: self-assessment only when required, observation 2 only when two are required. */
export function stagesFor(c: Pick<TemplateConfig, 'selfAssessment' | 'requiredObservations' | 'requiredConferences'>): CycleStage[] {
  const obs = c.requiredObservations.filter((o) => o.type === 'formal' || o.type === 'informal' || o.type === 'operational_review' || o.type === 'site_visit').reduce((n, o) => n + o.count, 0)
  return CYCLE_STAGES.filter((s) => {
    if (s === 'self_assessment') return c.selfAssessment.required
    if (s === 'observation_2') return obs >= 2
    if (s === 'mid_year') return c.requiredConferences.includes('mid_year')
    if (s === 'final_conference') return c.requiredConferences.includes('final')
    return true
  })
}

export const STAGE_LABELS: Record<CycleStage, string> = { self_assessment: 'Self-assessment', goals: 'Goals', observation: 'Observation 1', mid_year: 'Mid-year review', observation_2: 'Observation 2', final_conference: 'Final conference', final_evaluation: 'Final evaluation', signature: 'Signatures' }

const addCalendar = (d: string, n: number) => {
  const x = new Date(`${d}T12:00:00Z`)
  x.setUTCDate(x.getUTCDate() + n)
  return x.toISOString().slice(0, 10)
}

/** Adds working days; `isWork` decides (business days skip weekends; school days skip closures too). */
export function addWorkDays(d: string, n: number, isWork: (iso: string) => boolean): string {
  let cur = d
  let left = Math.abs(n)
  const step = n < 0 ? -1 : 1
  for (let guard = 0; left > 0 && guard < 800; guard += 1) {
    cur = addCalendar(cur, step)
    if (isWork(cur)) left -= 1
  }
  return cur
}

/** Stage deadlines from the template's rules, the cycle's window and the calendar. */
export function computeDeadlines(rules: TemplateConfig['deadlines'], startDate: string, dueDate: string, stages: CycleStage[], isSchoolDay: (d: string) => boolean): CycleDeadline[] {
  const business = (d: string) => ![0, 6].includes(new Date(`${d}T12:00:00Z`).getUTCDay())
  const out: CycleDeadline[] = []
  for (const stage of stages) {
    const r = rules.find((x) => x.stage === stage)?.rule
    let due: string
    if (!r) due = stage === 'signature' || stage === 'final_evaluation' ? dueDate : addCalendar(startDate, Math.round(((stages.indexOf(stage) + 1) / (stages.length + 1)) * dayDiff(startDate, dueDate)))
    else if (r.kind === 'fixed') due = r.date
    else {
      const from = r.kind === 'after_start' ? startDate : dueDate
      const n = r.kind === 'after_start' ? r.days : -r.days
      due = r.unit === 'calendar' ? addCalendar(from, n) : addWorkDays(from, n, r.unit === 'business' ? business : isSchoolDay)
    }
    out.push({ stage, dueOn: due, originalDueOn: due, adjustments: [] })
  }
  return out
}
const dayDiff = (a: string, b: string) => Math.round((Date.parse(`${b}T12:00:00Z`) - Date.parse(`${a}T12:00:00Z`)) / 86_400_000)

/** Moves deadlines (leave, transfer) without losing the originals. */
export function adjustDeadlines(ds: CycleDeadline[], days: number, from: CycleStage | null, by: { at: string; byName: string; reason: string }): CycleDeadline[] {
  let on = from == null
  return ds.map((d) => {
    if (d.stage === from) on = true
    if (!on || days === 0) return d
    const to = addCalendar(d.dueOn, days)
    return { ...d, dueOn: to, adjustments: [...d.adjustments, { ...by, from: d.dueOn, to }] }
  })
}

/** Overdue and due-soon from deadlines, against the current stage. */
export function evalDeadlineState(ds: CycleDeadline[], stage: CycleStage | null, today: string, soonDays = 14): { state: 'overdue' | 'due_soon' | 'on_track' | 'none'; dueOn: string | null } {
  const d = ds.find((x) => x.stage === stage)
  if (!d) return { state: 'none', dueOn: null }
  if (d.dueOn < today) return { state: 'overdue', dueOn: d.dueOn }
  if (dayDiff(today, d.dueOn) <= soonDays) return { state: 'due_soon', dueOn: d.dueOn }
  return { state: 'on_track', dueOn: d.dueOn }
}

/** The cycle status shown for a stage, before signatures. */
export function statusForStage(stage: CycleStage | null): CycleStatus {
  if (!stage) return 'not_started'
  if (stage === 'self_assessment') return 'self_assessment'
  if (stage === 'goals') return 'goals'
  if (stage === 'observation' || stage === 'observation_2') return 'observation'
  if (stage === 'mid_year') return 'mid_year_review'
  if (stage === 'signature') return 'awaiting_employee_signature'
  return 'final_evaluation'
}

// ── Bulk creation ───────────────────────────────────────────────────────────

export interface BulkCandidate {
  staffId: string
  name: string
  employeeType: EmployeeType
  templateFamily: string | null
  evaluatorStaffId: string | null
  evaluatorName: string | null
  issues: string[]
}

/** The preview: who, which template, who evaluates — and every reason a row would be a broken cycle. */
export function bulkSummary(rows: readonly BulkCandidate[]) {
  const conflicts = rows.filter((r) => r.issues.some((i) => /already has/.test(i))).length
  const missingEvaluator = rows.filter((r) => !r.evaluatorStaffId).length
  const missingTemplate = rows.filter((r) => !r.templateFamily).length
  const ready = rows.filter((r) => !r.issues.length && r.evaluatorStaffId && r.templateFamily).length
  return { employees: rows.length, ready, conflicts, missingEvaluator, missingTemplate }
}

// ─────────────────────────────────────────────────────────────────────────────
// Self-assessment and goals
// ─────────────────────────────────────────────────────────────────────────────

export const SELF_ASSESSMENT_STATUSES = ['not_started', 'in_progress', 'submitted', 'returned', 'complete'] as const
export type SelfAssessmentStatus = (typeof SELF_ASSESSMENT_STATUSES)[number]

/** The employee's own document; the draft is private to them. */
export interface EvalSelfAssessment extends BaseDocument {
  docType: 'evalSelfAssessment'
  cycleId: string
  staffId: string
  status: SelfAssessmentStatus
  entries: { domainKey: string; componentKey?: string | null; rating?: number | null; reflection?: string | null }[]
  overallReflection?: string | null
  proposedGoals?: string | null
  savedAt?: string | null
  submittedAt?: string | null
  returnedReason?: string | null
  history: { at: string; byName: string; action: string; note?: string | null }[]
}

export const GOAL_TYPES_DEFAULT = ['professional_practice', 'student_growth', 'leadership', 'operational', 'improvement', 'custom'] as const
export const GOAL_STATUSES_V2 = ['draft', 'submitted', 'approved', 'in_progress', 'completed', 'revised', 'closed'] as const
export type GoalStatusV2 = (typeof GOAL_STATUSES_V2)[number]

export interface EvalGoal extends BaseDocument {
  docType: 'evalGoal'
  cycleId: string
  staffId: string
  title: string
  description?: string | null
  goalType: string
  startDate?: string | null
  targetDate?: string | null
  successMeasure?: string | null
  status: GoalStatusV2
  createdByName: string
  createdByUserId: string
  approvedByName?: string | null
  approvedAt?: string | null
  evaluatorFeedback?: string | null
  employeeReflection?: string | null
  progress: { at: string; byName: string; narrative: string; percent?: number | null; status?: GoalStatusV2 | null; evidenceIds?: string[] }[]
  /** Every version of the wording, so an approval always refers to what was approved. */
  revisions: { at: string; byName: string; title: string; description?: string | null; successMeasure?: string | null; targetDate?: string | null }[]
  history: { at: string; byName: string; action: string; note?: string | null }[]
  carriedFromGoalId?: string | null
  growthGoalId?: string | null
}

/** Allowed goal transitions, by who is acting. */
export function goalTransition(from: GoalStatusV2, action: 'submit' | 'approve' | 'return' | 'start' | 'complete' | 'revise' | 'close', as: 'employee' | 'evaluator'): GoalStatusV2 | null {
  const t: Record<string, GoalStatusV2> = {
    'draft:submit:employee': 'submitted',
    'revised:submit:employee': 'submitted',
    'submitted:approve:evaluator': 'approved',
    'submitted:return:evaluator': 'revised',
    'approved:start:employee': 'in_progress',
    'approved:start:evaluator': 'in_progress',
    'in_progress:complete:employee': 'completed',
    'in_progress:complete:evaluator': 'completed',
    'approved:complete:evaluator': 'completed',
    'approved:revise:employee': 'revised',
    'in_progress:revise:employee': 'revised',
    'approved:revise:evaluator': 'revised',
    'in_progress:revise:evaluator': 'revised',
    'completed:close:evaluator': 'closed',
    'in_progress:close:evaluator': 'closed',
    'approved:close:evaluator': 'closed',
    'draft:close:employee': 'closed',
  }
  return t[`${from}:${action}:${as}`] ?? null
}

// ─────────────────────────────────────────────────────────────────────────────
// Who is looking
// ─────────────────────────────────────────────────────────────────────────────

export interface EvalViewer {
  isEmployee: boolean
  isEvaluator: boolean
  isSecondary: boolean
  /** On the approval chain, or the evaluator's supervisor. */
  isReviewer: boolean
  isHr: boolean
  /** A principal/admin over the school, without being the evaluator. */
  isSchoolAdmin: boolean
}

/** What a viewer may see of a cycle. Evaluator drafts reach the employee only once shared. */
export function evalFieldAccess(v: EvalViewer, shared: boolean) {
  const staffSide = v.isEvaluator || v.isSecondary || v.isReviewer || v.isHr || v.isSchoolAdmin
  return {
    summary: true,
    ratings: staffSide || (v.isEmployee && shared),
    narratives: staffSide || (v.isEmployee && shared),
    privateNotes: v.isEvaluator || v.isSecondary || v.isHr,
    selfAssessmentDraft: v.isEmployee,
    selfAssessmentSubmitted: true,
    restrictedEvidence: v.isHr,
    improvementPlan: v.isEmployee || v.isEvaluator || v.isHr,
    manage: v.isEvaluator || v.isHr,
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Defaults
// ─────────────────────────────────────────────────────────────────────────────

const MEANING = 'Signature acknowledges receipt and does not necessarily indicate agreement.'
const FOUR_LEVEL: RatingScale = {
  key: 'four-level',
  label: 'Ineffective – Exemplary',
  levels: [
    { value: 1, label: 'Ineffective', descriptor: 'Does not yet demonstrate the practice; immediate support is needed.' },
    { value: 2, label: 'Developing', descriptor: 'Demonstrates the practice inconsistently or partially.' },
    { value: 3, label: 'Accomplished', descriptor: 'Consistently demonstrates the practice with students and colleagues.' },
    { value: 4, label: 'Exemplary', descriptor: 'Demonstrates the practice at a high level and supports others in it.' },
  ],
  allowNotObserved: true,
}
const MEETS: RatingScale = {
  key: 'meets',
  label: 'Meets / Does Not Meet',
  levels: [
    { value: 1, label: 'Does Not Meet', descriptor: 'Performance falls short of the job’s expectations.' },
    { value: 2, label: 'Meets', descriptor: 'Performance meets the job’s expectations.' },
  ],
  allowNotObserved: false,
}
const comp = (key: string, label: string, sequence: number, o: Partial<EvalComponent> = {}): EvalComponent => ({ key, label, ratingRequired: true, narrativeRequired: false, evidenceRequired: false, sequence, ...o })
const signatures: SignatureRule[] = [
  { role: 'evaluator', order: 1, meaning: 'I conducted this evaluation and the ratings are my professional judgement.' },
  { role: 'employee', order: 2, meaning: MEANING, refusalAllowed: true, refusalWitnessRequired: true },
]
const chain: ApprovalRuleStep[] = [
  { key: 'evaluator-supervisor', label: 'Evaluator’s supervisor', approver: { kind: 'evaluator_supervisor' } },
  { key: 'hr', label: 'Human Resources', approver: { kind: 'roles', roles: ['human_resources'] } },
]

export const DEFAULT_TEMPLATES: Omit<EvaluationTemplateVersion, keyof BaseDocument | 'docType' | 'history'>[] = [
  {
    family: 'teacher',
    name: 'Teacher Evaluation',
    employeeTypes: ['teacher'],
    version: 1,
    status: 'published',
    effectiveStart: '2026-07-01',
    districtLocked: true,
    schoolCustomizable: [],
    config: {
      scale: FOUR_LEVEL,
      weighting: 'domain',
      overall: { mode: 'hybrid', overrideAllowed: true, overrideRequiresApproval: true },
      domains: [
        { key: 'planning', label: 'Instructional Planning', weight: 20, ratingRequired: false, narrativeRequired: true, sequence: 1, components: [comp('knowledge', 'Knowledge of content and students', 1), comp('outcomes', 'Clear learning outcomes', 2), comp('assessment-design', 'Assessment design', 3)] },
        { key: 'instruction', label: 'Instruction and Assessment', weight: 40, ratingRequired: false, narrativeRequired: true, sequence: 2, components: [comp('engagement', 'Engaging students in learning', 1, { evidenceRequired: true }), comp('questioning', 'Questioning and discussion', 2), comp('feedback', 'Using assessment and feedback', 3, { narrativeRequired: true })] },
        { key: 'environment', label: 'Learning Environment', weight: 20, ratingRequired: false, narrativeRequired: true, sequence: 3, components: [comp('respect', 'Culture of respect and rapport', 1), comp('procedures', 'Classroom procedures', 2), comp('behavior', 'Managing student behavior', 3)] },
        { key: 'professionalism', label: 'Professionalism', weight: 20, ratingRequired: false, narrativeRequired: true, sequence: 4, components: [comp('reflection', 'Reflecting on teaching', 1), comp('families', 'Communicating with families', 2), comp('growth', 'Growing professionally', 3)] },
      ],
      requiredObservations: [{ type: 'formal', count: 2, announced: 'either' }],
      requiredConferences: ['pre_observation', 'post_observation', 'mid_year', 'final'],
      goals: { min: 2, max: 4, approvalRequired: true, types: ['professional_practice', 'student_growth'] },
      selfAssessment: { required: true, evaluatorSeesOn: 'submit' },
      walkthroughsRollUp: 'when_included',
      unannouncedAllowed: true,
      approvalChain: chain,
      signatures,
      deadlines: [
        { stage: 'self_assessment', rule: { kind: 'after_start', days: 20, unit: 'school' } },
        { stage: 'goals', rule: { kind: 'after_start', days: 30, unit: 'school' } },
        { stage: 'observation', rule: { kind: 'fixed', date: '2026-12-11' } },
        { stage: 'mid_year', rule: { kind: 'fixed', date: '2027-01-29' } },
        { stage: 'observation_2', rule: { kind: 'fixed', date: '2027-03-26' } },
        { stage: 'final_conference', rule: { kind: 'before_due', days: 15, unit: 'school' } },
        { stage: 'final_evaluation', rule: { kind: 'before_due', days: 10, unit: 'school' } },
        { stage: 'signature', rule: { kind: 'before_due', days: 0, unit: 'calendar' } },
      ],
      improvementPlan: { mayRecommendBelow: 2, requiresHrApproval: true },
      evaluatorQualification: { training: 'Evaluator credential', warnOnly: true },
    },
  },
  {
    family: 'principal',
    name: 'Principal Evaluation',
    employeeTypes: ['principal', 'assistant_principal'],
    version: 1,
    status: 'published',
    effectiveStart: '2026-07-01',
    districtLocked: true,
    schoolCustomizable: [],
    config: {
      scale: FOUR_LEVEL,
      weighting: 'domain',
      overall: { mode: 'hybrid', overrideAllowed: true, overrideRequiresApproval: true },
      domains: [
        { key: 'vision', label: 'Vision and Mission', weight: 20, ratingRequired: true, narrativeRequired: true, sequence: 1, components: [] },
        { key: 'instructional', label: 'Instructional Leadership', weight: 35, ratingRequired: true, narrativeRequired: true, sequence: 2, components: [] },
        { key: 'operations', label: 'School Operations', weight: 25, ratingRequired: true, narrativeRequired: true, sequence: 3, components: [] },
        { key: 'community', label: 'Culture and Community', weight: 20, ratingRequired: true, narrativeRequired: true, sequence: 4, components: [] },
      ],
      requiredObservations: [{ type: 'site_visit', count: 2 }],
      requiredConferences: ['mid_year', 'final'],
      goals: { min: 2, max: 3, approvalRequired: true, types: ['leadership', 'student_growth'] },
      selfAssessment: { required: true, evaluatorSeesOn: 'submit' },
      walkthroughsRollUp: 'never',
      unannouncedAllowed: false,
      approvalChain: chain,
      signatures,
      deadlines: [],
      improvementPlan: { mayRecommendBelow: 2, requiresHrApproval: true },
    },
  },
  {
    family: 'classified',
    name: 'Classified Staff Evaluation',
    employeeTypes: ['classified', 'custodian', 'transportation', 'food_service', 'administrative', 'nurse', 'counselor', 'central_office'],
    version: 1,
    status: 'published',
    effectiveStart: '2026-07-01',
    districtLocked: false,
    schoolCustomizable: ['deadlines'],
    config: {
      scale: MEETS,
      weighting: 'none',
      overall: { mode: 'manual', overrideAllowed: false, overrideRequiresApproval: false },
      domains: [
        { key: 'knowledge', label: 'Job Knowledge', ratingRequired: true, narrativeRequired: false, sequence: 1, components: [] },
        { key: 'quality', label: 'Quality of Work', ratingRequired: true, narrativeRequired: false, sequence: 2, components: [] },
        { key: 'reliability', label: 'Reliability and Safety', ratingRequired: true, narrativeRequired: false, sequence: 3, components: [] },
        { key: 'teamwork', label: 'Communication and Teamwork', ratingRequired: true, narrativeRequired: true, sequence: 4, components: [] },
      ],
      requiredObservations: [{ type: 'operational_review', count: 1 }],
      requiredConferences: ['final'],
      goals: { min: 1, max: 3, approvalRequired: false, types: ['operational', 'professional_practice'] },
      selfAssessment: { required: false, evaluatorSeesOn: 'submit' },
      walkthroughsRollUp: 'never',
      unannouncedAllowed: true,
      approvalChain: [{ key: 'hr', label: 'Human Resources', approver: { kind: 'roles', roles: ['human_resources'] } }],
      signatures,
      deadlines: [],
      improvementPlan: { mayRecommendBelow: 1, requiresHrApproval: true },
    },
  },
]

export interface EvaluationSettingsDocument extends BaseDocument {
  docType: 'evaluationSettings'
  schoolYear: string
  yearStart: string
  yearEnd: string
  defaultFamily: string | null
  mappings: TemplateMappingRule[]
  evaluatorSource: EvaluatorSource
  /** Staff ids who evaluate by designation (department chairs, coaches). */
  designatedEvaluators: { staffId: string; employeeTypes: EmployeeType[]; departments: string[] }[]
  reopenRoles: string[]
  reminderDaysBefore: number[]
  smallGroupSuppression: number
  signatureMeaning: string
}

export const DEFAULT_EVALUATION_SETTINGS: Omit<EvaluationSettingsDocument, keyof BaseDocument | 'docType'> = {
  schoolYear: '2026-27',
  yearStart: '2026-08-17',
  yearEnd: '2027-05-28',
  defaultFamily: null,
  mappings: [
    { key: 'teachers', family: 'teacher', when: { employeeTypes: ['teacher'] }, priority: 10 },
    { key: 'principals', family: 'principal', when: { employeeTypes: ['principal', 'assistant_principal'] }, priority: 10 },
    { key: 'classified', family: 'classified', when: { employeeTypes: ['classified', 'custodian', 'transportation', 'food_service', 'administrative', 'nurse', 'counselor', 'central_office'] }, priority: 5 },
  ],
  evaluatorSource: 'direct_supervisor',
  designatedEvaluators: [],
  reopenRoles: ['human_resources', 'district_admin'],
  reminderDaysBefore: [14, 7, 1],
  smallGroupSuppression: 5,
  signatureMeaning: MEANING,
}

/** Thadeus's school year "2026-2027" and Staff's "2026-27" are the same year. */
export const normalizeSchoolYear = (y: string) => {
  const m = /^(\d{4})\D+(\d{2,4})$/.exec(y.trim())
  return m ? `${m[1]}-${m[2]!.slice(-2)}` : y
}


// ─────────────────────────────────────────────────────────────────────────────
// Stage 2: observations, evidence, conferences, the final evaluation
// ─────────────────────────────────────────────────────────────────────────────

export const OBSERVATION_STATUSES = ['scheduled', 'in_progress', 'draft', 'shared', 'completed', 'cancelled'] as const
export type ObservationStatus = (typeof OBSERVATION_STATUSES)[number]

export interface EvalObservation extends BaseDocument {
  docType: 'evalObservation'
  /** Null for a standalone walkthrough. */
  cycleId: string | null
  staffId: string
  evaluatorStaffId: string
  evaluatorName: string
  type: ObservationType
  announced: boolean
  date: string
  start?: string | null
  end?: string | null
  location?: string | null
  /** Teacher context: never student records, only what was taught where. */
  sectionId?: string | null
  sectionName?: string | null
  courseName?: string | null
  periodName?: string | null
  lessonContext?: string | null
  /** Non-teaching context: building/zone, route, program. */
  context?: string | null
  componentsObserved: string[]
  notes: { id: string; at: string; text: string; componentKeys: string[]; byName: string }[]
  /** Only where the template allows rating from an observation. */
  ratings?: Record<string, number | null>
  summary?: string | null
  followUp?: string | null
  conferenceRequired: boolean
  /** Walkthroughs: quick tags, and whether they count toward the evaluation. */
  tags?: string[]
  includedInEvaluation?: boolean
  status: ObservationStatus
  sharedAt?: string | null
  acknowledgedAt?: string | null
  history: { at: string; byName: string; action: string; note?: string | null }[]
}

export const EVIDENCE_VISIBILITY = ['employee_evaluator', 'evaluators_only', 'hr_restricted', 'final_evaluation'] as const
export type EvidenceVisibility = (typeof EVIDENCE_VISIBILITY)[number]
export const EVIDENCE_SOURCES = ['narrative', 'document', 'photo', 'link', 'work_sample', 'data', 'form'] as const

export interface EvalEvidence extends BaseDocument {
  docType: 'evalEvidence'
  cycleId: string
  staffId: string
  observationId?: string | null
  goalId?: string | null
  title: string
  description?: string | null
  sourceType: (typeof EVIDENCE_SOURCES)[number]
  date: string
  componentKeys: string[]
  blobPath?: string | null
  fileName?: string | null
  link?: string | null
  visibility: EvidenceVisibility
  /** Employee-submitted evidence goes through the evaluator. */
  status: 'draft' | 'submitted' | 'accepted' | 'returned'
  submittedByRole: 'employee' | 'evaluator' | 'hr'
  uploadedByName: string
  reviewNote?: string | null
  withdrawnAt?: string | null
  history: { at: string; byName: string; action: string; note?: string | null }[]
}

/** Who may see a piece of evidence. Final-evaluation evidence is shown once the evaluation is shared. */
export function evidenceVisible(e: Pick<EvalEvidence, 'visibility' | 'status' | 'submittedByRole' | 'withdrawnAt'>, v: EvalViewer, shared: boolean): boolean {
  if (e.withdrawnAt && !v.isHr) return false
  if (v.isHr) return true
  if (e.visibility === 'hr_restricted') return false
  if (v.isEvaluator || v.isSecondary) return e.status !== 'draft' || e.submittedByRole !== 'employee'
  if (v.isEmployee) return e.submittedByRole === 'employee' || e.visibility === 'employee_evaluator' || (e.visibility === 'final_evaluation' && shared)
  if (v.isReviewer || v.isSchoolAdmin) return e.visibility !== 'evaluators_only' || v.isReviewer
  return false
}

export interface EvalConference extends BaseDocument {
  docType: 'evalConference'
  cycleId: string
  staffId: string
  type: ConferenceType
  date: string
  start?: string | null
  end?: string | null
  location?: string | null
  participantStaffIds: string[]
  status: 'scheduled' | 'held' | 'cancelled'
  /** Evaluator's notes, private until shared with the conference record. */
  notes?: string | null
  actions: { text: string; ownerStaffId?: string | null; dueDate?: string | null; done?: boolean }[]
  followUp?: string | null
  /** Mid-year: interim ratings when the template uses them, and goal progress noted. */
  interimRatings?: Record<string, number | null>
  sharedAt?: string | null
  acknowledgements: { role: 'employee' | 'evaluator'; at: string; byName: string }[]
  observationId?: string | null
  history: { at: string; byName: string; action: string; note?: string | null }[]
}

export interface FinalRating {
  value: number | null
  notObserved?: boolean
  narrative?: string | null
  evidenceIds?: string[]
  observationIds?: string[]
  goalIds?: string[]
  byName: string
  at: string
}

export interface FinalEvaluation {
  ratings: Record<string, FinalRating>
  ratingHistory: { key: string; from: number | null; to: number | null; byName: string; at: string; reason?: string | null }[]
  narrative: { strengths?: string | null; growthAreas?: string | null; overallComments?: string | null; nextSteps?: string | null }
  overall: { calculated: number | null; chosen: number | null; explanation?: string | null; override?: { byName: string; at: string; reason: string; approvedByName?: string | null; approvedAt?: string | null } | null }
  improvementRecommended?: { reason: string; byName: string; at: string } | null
  savedAt?: string | null
  savedByName?: string | null
}

/** The key a rating is stored under: a domain, or `domain.component`. */
export const ratingKey = (domainKey: string, componentKey?: string | null) => (componentKey ? `${domainKey}.${componentKey}` : domainKey)

/**
 * The weighted overall, only where the template weights — never an invented
 * average. Not-observed components drop out and the remaining weights scale.
 */
export function calculatedOverall(c: Pick<TemplateConfig, 'weighting' | 'domains' | 'overall'>, ratings: Record<string, FinalRating>): number | null {
  if (c.overall.mode === 'manual' || c.overall.mode === 'none' || c.weighting === 'none') return null
  let total = 0
  let weight = 0
  for (const d of c.domains) {
    if (c.weighting === 'domain') {
      const own = ratings[ratingKey(d.key)]
      const comps = d.components.map((x) => ratings[ratingKey(d.key, x.key)]).filter((r) => r && r.value != null && !r.notObserved)
      const v = own && own.value != null && !own.notObserved ? own.value : comps.length ? comps.reduce((n, r) => n + (r!.value as number), 0) / comps.length : null
      if (v == null) continue
      total += v * (d.weight ?? 0)
      weight += d.weight ?? 0
    } else {
      for (const x of d.components) {
        const r = ratings[ratingKey(d.key, x.key)]
        if (!r || r.value == null || r.notObserved) continue
        const w = ((d.weight ?? 100) / 100) * (x.weight ?? 0)
        total += r.value * w
        weight += w
      }
    }
  }
  return weight > 0 ? Math.round((total / weight) * 100) / 100 : null
}

/** Nearest scale level to a calculated value (ties go up). */
export function nearestLevel(scale: RatingScale, value: number | null): RatingLevel | null {
  if (value == null) return null
  return [...scale.levels].sort((a, b) => Math.abs(a.value - value) - Math.abs(b.value - value) || b.value - a.value)[0] ?? null
}

/**
 * Exactly what stops the final evaluation from being shared: every required
 * rating, narrative and piece of evidence by name, the required observations
 * and conferences, and the overall rule.
 */
export function finalEvaluationGaps(c: TemplateConfig, f: FinalEvaluation | null | undefined, counts: { observations: Record<string, number>; conferences: string[]; evidenceByKey: Record<string, number>; approvedGoals: number }): string[] {
  const out: string[] = []
  const r = f?.ratings ?? {}
  for (const d of c.domains) {
    const own = r[ratingKey(d.key)]
    if (d.ratingRequired && (!own || (own.value == null && !own.notObserved))) out.push(`${d.label}: rating missing`)
    if (d.narrativeRequired && !own?.narrative?.trim()) out.push(`${d.label}: narrative missing`)
    for (const x of d.components) {
      const k = ratingKey(d.key, x.key)
      const rr = r[k]
      if (x.ratingRequired && (!rr || (rr.value == null && !rr.notObserved))) out.push(`${d.label} — ${x.label}: rating missing`)
      if (x.ratingRequired && rr?.notObserved && !c.scale.allowNotObserved) out.push(`${d.label} — ${x.label}: “not observed” is not allowed on this scale`)
      if (x.narrativeRequired && !rr?.narrative?.trim()) out.push(`${d.label} — ${x.label}: narrative missing`)
      if (x.evidenceRequired && !(rr?.evidenceIds?.length || counts.evidenceByKey[k] || rr?.observationIds?.length)) out.push(`${d.label} — ${x.label}: evidence missing`)
    }
  }
  for (const o of c.requiredObservations) {
    const have = counts.observations[o.type] ?? 0
    if (o.count > have) out.push(`${o.count} ${o.type.replace('_', ' ')} observation${o.count === 1 ? '' : 's'} required; ${have} completed`)
  }
  for (const k of c.requiredConferences) if (!counts.conferences.includes(k)) out.push(`${k.replace(/_/g, '-')} conference not held`)
  if (counts.approvedGoals < c.goals.min) out.push(`${c.goals.min} approved goal${c.goals.min === 1 ? '' : 's'} required; ${counts.approvedGoals} so far`)
  if (c.overall.mode !== 'none' && (f?.overall.chosen == null) && !(c.overall.mode === 'calculated' && f?.overall.calculated != null)) out.push('Overall rating missing')
  if (f?.overall.override && c.overall.overrideRequiresApproval && !f.overall.override.approvedAt) out.push('The overall-rating override is waiting for approval')
  if (!f?.narrative.overallComments?.trim()) out.push('Overall comments missing')
  return out
}

export interface EvalSignature {
  role: 'employee' | 'evaluator' | 'reviewer' | 'administrator'
  staffId: string | null
  userId: string
  name: string
  /** Drawn signature, when drawn. */
  image?: string | null
  meaning: string
  at: string
  /** sha256 of the content signed, so a signature always refers to one version. */
  contentHash: string
  version: number
  refused?: boolean
  witnessName?: string | null
  note?: string | null
}

/** The next signature the template's order calls for, given what is signed. */
export function nextSignature(rules: SignatureRule[], signed: Pick<EvalSignature, 'role'>[]): SignatureRule | null {
  const done = new Set(signed.map((s) => s.role))
  return [...rules].sort((a, b) => a.order - b.order).find((r) => !done.has(r.role)) ?? null
}

export const RETURN_AREAS = ['domain', 'narrative', 'evidence', 'rating', 'signature', 'other'] as const

/** The approval route after signatures, from the template chain. */
export function evaluationRoute(chain: ApprovalRuleStep[], evaluatorSupervisorUserId: string | null): { key: string; label: string; status: 'pending'; requires: { userIds?: string[]; roles?: string[] } }[] {
  return chain.map((s) => ({
    key: s.key,
    label: s.label,
    status: 'pending' as const,
    requires: s.approver.kind === 'evaluator_supervisor' ? (evaluatorSupervisorUserId ? { userIds: [evaluatorSupervisorUserId] } : { roles: ['district_admin'] }) : s.approver.kind === 'roles' ? { roles: s.approver.roles } : { userIds: s.approver.staffIds },
  }))
}

// ─────────────────────────────────────────────────────────────────────────────
// Stage 3: improvement plans, history, reports
// ─────────────────────────────────────────────────────────────────────────────

export const PLAN_STATUSES = ['draft', 'under_review', 'active', 'extended', 'completed', 'unsuccessful', 'closed', 'cancelled'] as const
export type ImprovementPlanStatus = (typeof PLAN_STATUSES)[number]
export const PLAN_STATUS_LABELS: Record<ImprovementPlanStatus, string> = { draft: 'Draft', under_review: 'Under Review', active: 'Active', extended: 'Extended', completed: 'Completed', unsuccessful: 'Unsuccessful', closed: 'Closed', cancelled: 'Cancelled' }
export const SUPPORT_KINDS = ['coaching', 'mentoring', 'training', 'observation', 'resources', 'check_in'] as const

/**
 * The improvement plan, as the Evaluations pass extends Thadeus's record. Not
 * disciplinary unless the district says so; an employment record with the
 * tightest audience: the employee, the plan owner, the reviewer and HR.
 */
export interface PlanV2Fields {
  reasonCategory: string
  startDate: string
  endDate: string
  ownerStaffId: string
  reviewerStaffId: string | null
  planStatus: ImprovementPlanStatus
  disciplinary: boolean
  actions: { id: string; description: string; ownerStaffId: string | null; dueDate: string | null; status: 'open' | 'in_progress' | 'done' | 'missed'; evidenceIds: string[]; completionNote?: string | null; completedAt?: string | null }[]
  supports: { id: string; kind: (typeof SUPPORT_KINDS)[number]; description: string; trainingId?: string | null }[]
  checkpoints: { id: string; date: string; heldAt?: string | null; notes?: string | null; progress?: string | null; followUp?: string | null; byName?: string | null }[]
  approval: { requiredBy: 'hr' | 'none'; approvedByName?: string | null; approvedAt?: string | null; returnedReason?: string | null } | null
  acknowledgedAt?: string | null
  extensions: { at: string; byName: string; from: string; to: string; reason: string }[]
  outcomeNote?: string | null
  recommendedFromCycleId?: string | null
  history: { at: string; byName: string; action: string; note?: string | null }[]
}

export function planTransition(from: ImprovementPlanStatus, action: 'submit' | 'approve' | 'return' | 'activate' | 'extend' | 'complete' | 'unsuccessful' | 'close' | 'cancel', hrApprovalRequired: boolean): ImprovementPlanStatus | null {
  const t: Record<string, ImprovementPlanStatus> = {
    'draft:submit': hrApprovalRequired ? 'under_review' : 'active',
    'draft:activate': hrApprovalRequired ? 'draft' : 'active',
    'under_review:approve': 'active',
    'under_review:return': 'draft',
    'active:extend': 'extended',
    'extended:extend': 'extended',
    'active:complete': 'completed',
    'extended:complete': 'completed',
    'active:unsuccessful': 'unsuccessful',
    'extended:unsuccessful': 'unsuccessful',
    'completed:close': 'closed',
    'unsuccessful:close': 'closed',
    'draft:cancel': 'cancelled',
    'under_review:cancel': 'cancelled',
    'active:cancel': 'cancelled',
  }
  const next = t[`${from}:${action}`] ?? null
  return next === from ? null : next
}

/** Factual small-group suppression for restricted reports: below the threshold, a count reads "fewer than N". */
export function suppress(n: number, threshold: number): number | string {
  return n > 0 && n < threshold ? `<${threshold}` : n
}

/** Domain-by-domain comparison of finalized cycles — the ratings as given, never a trend score. */
export function compareCycles(cycles: { id: string; schoolYear: string; domains: { key: string; label: string }[]; ratings: Record<string, { value: number | null; label?: string | null }>; overallLabel: string | null }[]) {
  const keys = [...new Map(cycles.flatMap((c) => c.domains.map((d) => [d.key, d.label] as const))).entries()]
  return {
    columns: cycles.map((c) => ({ id: c.id, schoolYear: c.schoolYear, overall: c.overallLabel })),
    rows: keys.map(([key, label]) => ({ key, label, values: cycles.map((c) => c.ratings[key]?.label ?? (c.ratings[key]?.value != null ? String(c.ratings[key]!.value) : '—')) })),
  }
}
